/**
 * Types, parsing, and page-diffing for BIM-Hub's `bim-hub.model-chunks/1` manifest -- the read side
 * of the D3 save "second save" fix. Ported/adapted (not guessed) from Editor2's own
 * `frontend/src/lib/model-storage.ts` (`ModelChunkManifest`/`ModelPageChanges`/
 * `parseModelChunkManifest`/`diffModelChunkPages`), the exact source the read contract
 * (`bim-hub-integration/reports/2026-08-13-d3-second-save-base-revision-null-bug.md`'s answer,
 * `orchestrator-integration/reports/2026-08-13-model-read-contract-for-workbench.md` §2.3/§3 in the
 * sefket-editor-development workspace) named as copyable.
 *
 * Why this exists at all: `ModelStorageSession.hydrate(manifest_json, manifest_sha256,
 * content_objects)` (the vendored `bim-hub-model-storage` WASM) needs the CURRENT manifest read back
 * from BIM-Hub before a second-or-later `.update()` can produce a manifest with the correct
 * `base_revision` (see `src/server/d3-save.ts`'s own doc comment on `runD3Save` for the full
 * root-cause explanation: `.create()` always writes `base_revision: null`, which is only correct for
 * a first save). `page_changes` is a SEPARATE requirement discovered by the same read-contract
 * report §3: BIM-Hub's own `begin_client_chunk_upload` guard requires `base_revision.is_some() ==
 * page_changes.is_some()` -- present in the wire, but computed ENTIRELY client-side (WASM does not
 * expose a page-diff function) by comparing the hydrated (base) manifest against the updated
 * (target) manifest. Internal field names here are camelCase, matching this codebase's own
 * convention elsewhere (e.g. `property-proposal-request.ts`) -- translated to the wire's snake_case
 * shape only at the `bim-hub-client.ts` request-building boundary, not carried snake_case throughout.
 */

export const MODEL_CHUNK_SCHEMA = "bim-hub.model-chunks/1" as const;

const SHA256_HEX = /^[0-9a-f]{64}$/;
const PAGE_ID_RE = /^pg_[0-9a-f]{32}$/;
const IDENTITY_KEYS = new Set(["id", "guid", "element_id"]);

export interface ModelChunkContentRef {
  readonly digest: string;
  readonly bytes: number;
}

export interface ModelChunkPageRef extends ModelChunkContentRef {
  readonly pageId: string;
  readonly itemCount: number;
  readonly oversized: boolean;
}

export interface ModelChunkCollection {
  readonly path: string;
  readonly identityKey: "id" | "guid" | "element_id";
  readonly pages: readonly ModelChunkPageRef[];
}

export interface ModelChunkManifest {
  readonly schema: typeof MODEL_CHUNK_SCHEMA;
  readonly projectId: string;
  readonly modelId: string;
  readonly revision: number;
  readonly baseRevision: number | null;
  readonly targetChunkBytes: number;
  readonly metadata: ModelChunkContentRef;
  readonly collections: readonly ModelChunkCollection[];
}

export interface ModelPageBaseTarget {
  readonly path: string;
  readonly pageId: string;
  readonly baseDigest: string;
  readonly targetDigest: string | null;
}

export interface ModelPageAddition {
  readonly path: string;
  readonly pageId: string;
  readonly targetDigest: string;
}

export interface ModelPageChanges {
  readonly baseRevision: number;
  readonly targetRevision: number;
  readonly metadata: { readonly baseDigest: string; readonly targetDigest: string } | null;
  /** Existing logical pages whose canonical bytes changed or were removed. */
  readonly targets: readonly ModelPageBaseTarget[];
  /** New logical pages have no `{pageId,baseDigest}` precondition. */
  readonly additions: readonly ModelPageAddition[];
  /** `true` when collection identity/path/page order changed. Rebase must fail closed. */
  readonly layoutChanged: boolean;
  readonly changedCollections: readonly string[];
}

export class ModelChunkManifestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ModelChunkManifestError";
  }
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new ModelChunkManifestError(`${label} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function positiveInteger(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value <= 0) {
    throw new ModelChunkManifestError(`${label} must be a positive safe integer.`);
  }
  return value;
}

function digestString(value: unknown, label: string): string {
  if (typeof value !== "string" || !SHA256_HEX.test(value)) {
    throw new ModelChunkManifestError(`${label} must be a 64-char lowercase hex SHA-256.`);
  }
  return value;
}

function contentRef(value: unknown, label: string): ModelChunkContentRef {
  const ref = record(value, label);
  return { digest: digestString(ref.digest, `${label}.digest`), bytes: positiveInteger(ref.bytes, `${label}.bytes`) };
}

/**
 * Parses the versioned manifest without trusting a TypeScript cast -- scope (project/model/revision)
 * is re-checked here even though BIM-Hub already checked it server-side, matching Editor2's own
 * doc comment: "a correctly hashed manifest from another project/model/revision is still the wrong
 * payload."
 */
export function parseModelChunkManifest(
  value: unknown,
  expected: { readonly projectId: string; readonly modelId: string; readonly revision?: number }
): ModelChunkManifest {
  const raw = record(value, "manifest");
  if (raw.schema !== MODEL_CHUNK_SCHEMA) throw new ModelChunkManifestError("Unsupported model chunk manifest schema.");
  if (raw.project_id !== expected.projectId || raw.model_id !== expected.modelId) {
    throw new ModelChunkManifestError("Model chunk manifest scope mismatch.");
  }
  const revision = positiveInteger(raw.revision, "manifest.revision");
  if (expected.revision !== undefined && revision !== expected.revision) {
    throw new ModelChunkManifestError("Model chunk manifest revision mismatch.");
  }
  const baseRevision = raw.base_revision === null ? null : positiveInteger(raw.base_revision, "manifest.base_revision");
  if (baseRevision !== null && baseRevision >= revision) {
    throw new ModelChunkManifestError("manifest.base_revision must precede revision.");
  }
  const targetChunkBytes = positiveInteger(raw.target_chunk_bytes, "manifest.target_chunk_bytes");
  if (!Array.isArray(raw.collections)) throw new ModelChunkManifestError("manifest.collections must be an array.");

  const contentBytes = new Map<string, number>();
  const registerContentRef = (ref: ModelChunkContentRef): ModelChunkContentRef => {
    const previous = contentBytes.get(ref.digest);
    if (previous !== undefined && previous !== ref.bytes) {
      throw new ModelChunkManifestError("One manifest content digest declares conflicting byte lengths.");
    }
    contentBytes.set(ref.digest, ref.bytes);
    return ref;
  };
  const metadata = registerContentRef(contentRef(raw.metadata, "manifest.metadata"));
  const pageIds = new Set<string>();
  let priorPath: string | null = null;
  const collections: ModelChunkCollection[] = raw.collections.map((entry, collectionIndex) => {
    const collection = record(entry, `manifest.collections[${collectionIndex}]`);
    if (typeof collection.path !== "string" || !collection.path.startsWith("/") || (priorPath !== null && priorPath >= collection.path)) {
      throw new ModelChunkManifestError("Manifest collection paths must be unique and sorted.");
    }
    priorPath = collection.path;
    if (typeof collection.identity_key !== "string" || !IDENTITY_KEYS.has(collection.identity_key)) {
      throw new ModelChunkManifestError("Unsupported manifest collection identity key.");
    }
    if (!Array.isArray(collection.pages)) throw new ModelChunkManifestError("manifest collection pages must be an array.");
    const pages: ModelChunkPageRef[] = collection.pages.map((pageValue, pageIndex) => {
      const page = record(pageValue, `manifest.collections[${collectionIndex}].pages[${pageIndex}]`);
      if (typeof page.page_id !== "string" || !PAGE_ID_RE.test(page.page_id) || pageIds.has(page.page_id)) {
        throw new ModelChunkManifestError("Manifest page ids must be unique canonical ids.");
      }
      pageIds.add(page.page_id);
      const ref = registerContentRef(contentRef(page, "manifest page"));
      const itemCount = positiveInteger(page.item_count, "manifest page item_count");
      if (typeof page.oversized !== "boolean" || page.oversized !== ref.bytes > targetChunkBytes) {
        throw new ModelChunkManifestError("Manifest page oversized marker is inconsistent.");
      }
      return { pageId: page.page_id, digest: ref.digest, bytes: ref.bytes, itemCount, oversized: page.oversized };
    });
    return { path: collection.path, identityKey: collection.identity_key as ModelChunkCollection["identityKey"], pages };
  });

  return {
    schema: MODEL_CHUNK_SCHEMA,
    projectId: expected.projectId,
    modelId: expected.modelId,
    revision,
    baseRevision,
    targetChunkBytes,
    metadata,
    collections
  };
}

/** `{metadata.digest} ∪ {every collections[].pages[].digest}`, deduplicated -- exactly the set of
 *  content objects `.hydrate()` needs, per the read contract's own §1.3 formula. */
export function manifestContentDigests(manifest: ModelChunkManifest): readonly string[] {
  const digests = new Set<string>([manifest.metadata.digest]);
  for (const collection of manifest.collections) {
    for (const page of collection.pages) digests.add(page.digest);
  }
  return [...digests];
}

/**
 * Compares two native manifests without reading content bytes -- ported verbatim (field names
 * translated to camelCase) from Editor2's `diffModelChunkPages`. Existing pages carry
 * `{pageId,baseDigest}`; page additions are deliberately separate because inventing a base digest
 * would make an unsafe rebase look fenced.
 */
export function diffModelChunkPages(base: ModelChunkManifest, target: ModelChunkManifest): ModelPageChanges {
  if (
    base.projectId !== target.projectId ||
    base.modelId !== target.modelId ||
    target.baseRevision !== base.revision ||
    target.revision !== base.revision + 1
  ) {
    throw new ModelChunkManifestError("Model page diff scope or revision mismatch.");
  }

  const baseCollections = new Map(base.collections.map((collection) => [collection.path, collection]));
  const targetCollections = new Map(target.collections.map((collection) => [collection.path, collection]));
  const paths = new Set([...baseCollections.keys(), ...targetCollections.keys()]);
  const targets: ModelPageBaseTarget[] = [];
  const additions: ModelPageAddition[] = [];
  const changedCollections = new Set<string>();
  let layoutChanged = base.targetChunkBytes !== target.targetChunkBytes;

  for (const path of paths) {
    const before = baseCollections.get(path);
    const after = targetCollections.get(path);
    if (!before || !after || before.identityKey !== after.identityKey) {
      layoutChanged = true;
      changedCollections.add(path);
    }
    const beforePages = new Map((before?.pages ?? []).map((page) => [page.pageId, page]));
    const afterPages = new Map((after?.pages ?? []).map((page) => [page.pageId, page]));
    const beforeOrder = (before?.pages ?? []).map((page) => page.pageId);
    const afterOrder = (after?.pages ?? []).map((page) => page.pageId);
    if (beforeOrder.length !== afterOrder.length || beforeOrder.some((pageId, index) => pageId !== afterOrder[index])) {
      layoutChanged = true;
    }

    for (const page of before?.pages ?? []) {
      const next = afterPages.get(page.pageId);
      if (!next || next.digest !== page.digest) {
        changedCollections.add(path);
        targets.push({ path, pageId: page.pageId, baseDigest: page.digest, targetDigest: next?.digest ?? null });
      } else if (next.bytes !== page.bytes || next.itemCount !== page.itemCount || next.oversized !== page.oversized) {
        layoutChanged = true;
        changedCollections.add(path);
      }
    }
    for (const page of after?.pages ?? []) {
      if (!beforePages.has(page.pageId)) {
        changedCollections.add(path);
        additions.push({ path, pageId: page.pageId, targetDigest: page.digest });
      }
    }
  }

  const byPathAndPage = (left: { path: string; pageId: string }, right: { path: string; pageId: string }): number =>
    left.path === right.path
      ? left.pageId === right.pageId
        ? 0
        : left.pageId < right.pageId
          ? -1
          : 1
      : left.path < right.path
        ? -1
        : 1;
  targets.sort(byPathAndPage);
  additions.sort(byPathAndPage);

  return {
    baseRevision: base.revision,
    targetRevision: target.revision,
    metadata: base.metadata.digest === target.metadata.digest ? null : { baseDigest: base.metadata.digest, targetDigest: target.metadata.digest },
    targets,
    additions,
    layoutChanged,
    changedCollections: [...changedCollections].sort()
  };
}
